/**
 * SmartStart admin card — DoorlyDesk Closings tab companion to the CTC
 * checklist. Renders only when the borrower is enrolled. States:
 *
 *   - Enrolled, locked (no unlocked_at)   — yellow pill, "Pending Home Shopping"
 *   - Enrolled, in progress               — module-by-module status grid
 *   - Enrolled, complete                  — green pill + cert link + completion date
 *
 * Backed by GET /api/admin/users/:id/smartstart (returns the same shape
 * as the borrower /api/smartstart/status endpoint).
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const PRIMARY = '#3a6ad9';
const NEAR_BLACK = '#111111';
const LIME = '#acf44e';

const REASON_LABEL = { low_fico: 'Low FICO (<620)', first_time_homebuyer: 'First-Time Homebuyer' };

export default function SmartStartAdminCard({ userId, headers }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/admin/users/${userId}/smartstart`, { headers });
      setStatus(r.data);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [userId, headers]);

  useEffect(() => { load(); }, [load]);

  if (loading || !status?.enrolled) return null;

  const completed = !!status.completedAt;
  const locked = !status.unlockedAt;
  const completedCount = status.completedCount || 0;
  const totalModules = status.totalModules || 6;
  const reasons = (status.enrollmentReason || []).map((r) => REASON_LABEL[r] || r).join(', ') || '—';
  const totalSeconds = (status.progress || []).reduce((s, p) => s + (p.time_spent_seconds || 0), 0);
  const totalMinutes = Math.round(totalSeconds / 60);

  const card = {
    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
    padding: 18, marginTop: 16,
  };
  const pill = (bg, color, label) => (
    <span style={{ fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 999, background: bg, color, textTransform: 'uppercase', letterSpacing: 0.06 }}>
      {label}
    </span>
  );

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 800, color: NEAR_BLACK, textTransform: 'uppercase', letterSpacing: 0.06 }}>
            SmartStart™
          </span>
          {completed && pill('#f0fde4', '#166534', '✓ Complete')}
          {!completed && locked && pill('#fffbeb', '#92400e', 'Pending Home Shopping')}
          {!completed && !locked && pill('#eff3fe', PRIMARY, 'In Progress')}
        </div>
        <div style={{ fontSize: 12, fontWeight: 700, color: completed ? '#166534' : NEAR_BLACK, fontVariantNumeric: 'tabular-nums' }}>
          {completedCount} / {totalModules}
        </div>
      </div>

      <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 10 }}>
        Reason: {reasons} · Enrolled {status.enrolledAt ? new Date(status.enrolledAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
      </div>

      {/* Per-module grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 6, marginBottom: 10 }}>
        {(status.progress || []).slice(0, totalModules).map((p) => {
          const num = p.module_number;
          const c = !!p.completed_at;
          const inProg = !!p.started_at && !c;
          let bg = '#f3f4f6'; let color = '#9ca3af'; let glyph = '○';
          if (c) { bg = '#f0fde4'; color = '#166534'; glyph = '✓'; }
          else if (inProg) { bg = '#fffbeb'; color = '#92400e'; glyph = '🔄'; }
          return (
            <div key={num}
              style={{ background: bg, color, borderRadius: 8, padding: '8px 4px', textAlign: 'center', fontSize: 11, fontWeight: 700 }}>
              <div style={{ fontSize: 10, opacity: 0.8 }}>M{num}</div>
              <div style={{ fontSize: 14, marginTop: 2 }}>{glyph}</div>
              <div style={{ fontSize: 10, marginTop: 2, fontWeight: 700 }}>
                {p.quiz_score != null ? `${p.quiz_score}/3` : '—'}
              </div>
            </div>
          );
        })}
      </div>

      {/* Cumulative progress bar */}
      <div style={{ height: 6, background: '#f3f4f6', borderRadius: 999, overflow: 'hidden', marginBottom: 8 }}>
        <div style={{ height: '100%', width: `${(completedCount / totalModules) * 100}%`, background: completed ? LIME : PRIMARY, transition: 'width 0.3s ease' }} />
      </div>

      <div style={{ fontSize: 11, color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
        <span>{totalMinutes > 0 ? `~${totalMinutes} min spent` : 'Not started'}</span>
        {completed && status.certificateDocumentId && (
          <a href={`${API}/api/admin/users/${userId}/documents/${status.certificateDocumentId}/download`} target="_blank" rel="noopener noreferrer"
            style={{ color: PRIMARY, textDecoration: 'none', fontWeight: 700 }}>
            View Certificate →
          </a>
        )}
      </div>

      {!completed && (
        <div style={{ marginTop: 10, padding: '8px 10px', background: '#fffbef', border: '1px solid #ffe4a0', borderRadius: 6, fontSize: 11, color: '#7a5200' }}>
          ⚠ Must be completed before closing. CTC will not auto-fire while SmartStart is incomplete.
        </div>
      )}
    </div>
  );
}
